import { downStreamExternalSystemIntegrationTypes } from 'src/constants';
import { IAggregator } from 'src/types/aggregator.types';

/**
 * A downstream channel that pricing can address.
 *
 * The pricing service keys prices and rules on `extension_instance_id`, not on the
 * external system id that `IAggregator.aggregatorId` carries. Use `extensionInstanceId`
 * whenever building a pricing form field, price payload or price rule.
 */
export type IPricingChannel = IAggregator & { extensionInstanceId: string; extensionInstanceName: string };

/**
 * Narrows the brand's external systems to the downstream channels pricing can address.
 *
 * An external system yields one channel per resolvable extension instance: its own
 * `extensionInstanceId` when there is exactly one unambiguous instance, otherwise one
 * channel per entry in `configuredAddons` (covers both a single addon with several
 * instances, and several addons sharing one external system). An external system with
 * no instances at all yields no channels, rather than being bound to an arbitrary one.
 */
export const getPricingChannels = (externalSystems: IAggregator[] | undefined): IPricingChannel[] =>
  (externalSystems ?? [])
    .filter((externalSystem) => downStreamExternalSystemIntegrationTypes.includes(externalSystem.integrationType))
    .flatMap((externalSystem): IPricingChannel[] => {
      if (externalSystem.extensionInstanceId && externalSystem.extensionInstanceName) {
        return [externalSystem as IPricingChannel];
      }
      return externalSystem.configuredAddons
        .filter((addon) => Boolean(addon.instanceId))
        .map((addon) => ({
          ...externalSystem,
          extensionInstanceId: addon.instanceId,
          extensionInstanceName: addon.instanceName,
        }));
    });

/**
 * How a channel is named in the UI.
 *
 * Instance names are chosen by the user and often say nothing about the extension they belong to
 * ("UK", "Region 2"), so both are shown: `Oracle - UK`. Systems whose single instance repeats the
 * extension name are left as-is rather than reading `Deliveroo - Deliveroo`.
 */
export const getChannelLabel = ({
  name,
  extensionInstanceName,
}: Pick<IPricingChannel, 'name' | 'extensionInstanceName'>): string => {
  if (!name) return extensionInstanceName;
  if (!extensionInstanceName || extensionInstanceName === name) return name;
  return `${name} - ${extensionInstanceName}`;
};
